<i18n src="./resources/locales.json"></i18n>

<template>
  <v-card flat color="transparent" class="search-input-container">
    <v-row
      class="inner-container d-inline-flex"
      :class="{ small }"
      :style="CSS_VARS"
      align="center"
      align-content="center"
      no-gutters
    >
      <v-icon v-show="!searchText" class="search-input-icon d-inline-block ma-1 pl-1" color="inherit">
        mdi-magnify
      </v-icon>
      <input
        ref="searchInput"
        v-model.trim="searchText"
        type="search"
        class="search-input d-inline-flex"
        :aria-label="placeholder"
        :placeholder="placeholder"
        @input="handleInput"
        @keyup.enter="onEnter"
      />
      <v-btn v-show="showCloseButton" role="button" class="search-clear-icon" color="inherit" icon small @click="clear">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
      <div v-show="showFiltersButton">
        <v-menu bottom :close-on-content-click="false" close-on-click offset-y left>
          <template v-slot:activator="{ on }">
            <v-btn role="button" class="filter-btn" icon small v-on="on">
              <v-icon :color="filterButtonColor" class="search-filter-icon">
                mdi-filter
              </v-icon>
            </v-btn>
          </template>

          <div class="filters-container custom-scrollbar">
            <v-select
              v-for="group in filters"
              :key="group.key"
              v-model="selectedFilters[group.key]"
              :items="group.options"
              :label="group.title"
              item-text="label"
              item-value="value"
              multiple
              small-chips
              deletable-chips
            />

            <v-row>
              <v-spacer />
              <v-btn text color="primary" @click="clearFilters">
                <v-icon left small>mdi-delete</v-icon>
                Clear filters
              </v-btn>
            </v-row>
          </div>
        </v-menu>
      </div>
      <slot name="append-icon"></slot>
    </v-row>
  </v-card>
</template>

<script>
import debounce from 'lodash/debounce';

const SEARCH_INPUT_CHANGED_EVENT = 'search-input-changed';

export default {
  name: 'EcsSearchInput',

  props: {
    sync: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array,
      default: () => [],
      example: [
        {
          key: 'FILTER_A',
          title: 'Foo',
          options: [
            { label: 'Filter A.A', value: 0 },
            { label: 'Filter A.B', value: 1 }
          ]
        },
        {
          key: 'FILTER_B',
          title: 'Bar',
          options: [{ label: 'Filter B.A', value: 0 }]
        }
      ]
    },
    placeholder: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      selectedFilters: {},
      searchText: '',
      radioGroup: 0
    };
  },

  computed: {
    CSS_VARS() {
      let widthOffset = 36;
      if (this.showCloseButton || this.showFiltersButton) {
        widthOffset += 32;
        if (this.showCloseButton && this.showFiltersButton) {
          widthOffset += 32;
        }
      }
      return {
        '--calc-search-width': `calc(100% - ${widthOffset}px)`
      };
    },

    activeFilters() {
      return Object.keys(this.selectedFilters).length > 0;
    },

    showCloseButton() {
      return !!this.searchText;
    },

    showFiltersButton() {
      return this.filters.length > 0;
    },

    filterButtonColor() {
      return this.activeFilters ? 'primary' : 'inherit';
    }
  },

  watch: {
    selectedFilters: {
      handler() {
        Object.keys(this.selectedFilters).forEach(filterKey => {
          if (this.selectedFilters[filterKey].length === 0) {
            delete this.selectedFilters[filterKey];
          }
        });
        this.$emit('search-filters-changed', this.selectedFilters);
      },
      deep: true
    }
  },

  methods: {
    clear() {
      this.searchText = '';
      this.$refs.searchInput.focus();
      this.$emit(SEARCH_INPUT_CHANGED_EVENT, this.searchText);
    },

    clearFilters() {
      this.$refs.searchInput.focus();
      this.selectedFilters = {};
    },

    setActiveFilter(key, value) {
      if (this.selectedFilters[key]) {
        this.selectedFilters[key].push(value);
      } else {
        this.selectedFilters = {
          ...this.selectedFilters,
          [key]: [value]
        };
      }
    },

    handleInput() {
      this.sync ? this.handleSyncInput() : this.handleDebouncedInput();
    },

    handleSyncInput() {
      this.$emit(SEARCH_INPUT_CHANGED_EVENT, this.searchText);
    },

    handleDebouncedInput: debounce(function() {
      this.$emit(SEARCH_INPUT_CHANGED_EVENT, this.searchText);
    }, 250),

    onEnter() {
      this.$emit('search-input-enter', this.searchText);
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.search-input-container {
  position: relative;
  min-width: 200px;
  width: 100%;
  z-index: 2;
}

.inner-container {
  position: relative;
  border: 1px solid var(--v-border-color);
  border-radius: 24px !important;
  z-index: 1;
  background: var(--v-surface-base);
  width: 100%;
}

.inner-container.small {
  height: 28px;
}

.inner-container.small .search-input {
  font-size: 12px;
}

.inner-container.small .search-clear-icon,
.inner-container.small .search-input-icon {
  font-size: 20px;
}

.inner-container.small .filter-btn {
  position: absolute;
  top: 0;
  right: 0;
  width: 26px;
  height: 26px;
}

.inner-container.small .search-filter-icon {
  font-size: 16px;
}

.search-input-icon {
  z-index: 1;
}

.inner-container.small .search-clear-icon {
  position: absolute;
  right: 24px;
}

.search-input {
  color: inherit;
  line-height: 16px;
  font-size: 16px;
  background-color: transparent;
  outline: none;
  border: 0;
  flex-grow: 2;
}

::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: inherit;
  opacity: 0.4; /* Firefox */
}

:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: inherit;
}

::-ms-input-placeholder {
  /* Microsoft Edge */
  color: inherit;
}

.search-filter-icon {
  z-index: 1;
}

.filters-container {
  width: 360px;
  max-width: 100%;
  padding: 20px;
  background: var(--v-surface-base);
}
</style>

<style lang="scss">
.filters-container {
  .v-chip.v-size--small {
    border-radius: 12px;
    font-size: 11px;
    height: 18px;
    line-height: 18px;
  }
  .v-chip__close {
    font-size: 16px !important;
    margin: -2px -11px 0 4px !important;
    display: block !important;
    opacity: 0.5;
  }
  .v-label.v-label--active {
    top: 0;
  }
}
.filters-v-select {
  .v-list-item {
    min-height: 20px;
    height: 36px;
    padding: 0 12px;
  }
  .v-list-item__action {
    margin: 0 16px 0 0;
  }
}
.search-input-container .v-menu__content.theme--dark {
  background-color: #1e1e1e;
}
.search-input-container .v-menu__content.theme--light {
  background-color: #ffffff;
}
</style>
